import { useNavigate } from "react-router-dom";
import { ArrowRight, BadgeCheck, CreditCard, Headset, Wand2 } from "lucide-react";
import { useLang } from "../i18n";
import { useSiteSettings, goTo, pick } from "../settings";
import { MaskedLine, FadeUp } from "./Reveal";
import { BrowserFrame, scr } from "./Frames";

const FALLBACK_IMG = scr("web-bireysel-panel");

// ikas tarzı ortalanmış hero: başlık + rozet satırı + e-posta formu + gri iş ortağı logoları.
// Başlık/açıklama admin panelinden (Site İçeriği > Hero) yönetilmeye devam eder.
// Not: Başlığın sağındaki/solundaki 4 ekran kartı kullanıcı isteğiyle kaldırıldı (2026-10-02).
export const Hero = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const settings = useSiteSettings();
  const hero = settings.hero || {};

  const first = hero.slides?.length ? hero.slides[0] : null;
  const content = {
    line1: pick(first || hero, "line1", lang, t.hero.line1),
    line2: pick(first || hero, "line2", lang, t.hero.line2),
    desc: first ? pick(first, "desc", lang, t.hero.sub) : pick(hero, "sub", lang, t.hero.sub),
    ctaLabel:
      (first && (lang === "en" ? first.cta_label_en || first.cta_label_tr : first.cta_label_tr)) ||
      t.hero.ctaPrimary,
    img: first?.image || FALLBACK_IMG,
  };
  const badges = [
    { icon: BadgeCheck, tr: "ParasyoFree ömür boyu ücretsiz", en: "ParasyoFree free for life" },
    { icon: CreditCard, tr: "Kredi kartı gerekmez", en: "No credit card required" },
    { icon: Headset, tr: "7/24 canlı destek", en: "24/7 live support" },
  ];

  return (
    <section
      className="relative overflow-hidden bg-operofy-light pt-[calc(64px+var(--announcement-height,0px))]"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-[380px] w-[720px] -translate-x-1/2 rounded-full bg-operofy-blue/10 blur-3xl" />
      <div className="relative z-10 mx-auto max-w-7xl px-6 pb-16 pt-14 lg:px-8 lg:pb-20 lg:pt-20">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="font-display font-extrabold tracking-tight text-black" data-testid="hero-heading">
            <MaskedLine className="text-4xl leading-[1.1] sm:text-5xl lg:text-[3.6rem]">{content.line1}</MaskedLine>
            <MaskedLine delay={0.12} className="text-4xl leading-[1.1] sm:text-5xl lg:text-[3.6rem]">
              <span className="text-operofy-blue">{content.line2}</span>
            </MaskedLine>
          </h1>
          <FadeUp delay={0.2}>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-black/55 md:text-lg" data-testid="hero-sub">
              {content.desc}
            </p>
          </FadeUp>

          <FadeUp delay={0.28}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3" data-testid="hero-note">
              {badges.map((b) => {
                const Icon = b.icon;
                return (
                  <span key={b.tr} className="inline-flex items-center gap-2 text-sm font-semibold text-black/60">
                    <Icon size={16} strokeWidth={1.8} className="text-operofy-blue" />
                    {lang === "en" ? b.en : b.tr}
                  </span>
                );
              })}
            </div>
          </FadeUp>

          <FadeUp delay={0.36}>
            <div className="mx-auto mt-9 flex max-w-xl flex-col items-center gap-4" data-testid="hero-plan-finder">
              <button
                onClick={() => goTo("/hangi-paket", navigate)}
                data-testid="hero-plan-finder-btn"
                className="group inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-2xl bg-operofy-blue px-8 py-4 text-sm font-bold text-white transition-all duration-200 hover:bg-operofy-bluehover active:scale-95"
              >
                <Wand2 size={16} strokeWidth={2} />
                {lang === "en" ? "Find your plan in a few questions" : "Birkaç soruda size uygun paketi bulalım"}
                <ArrowRight size={15} strokeWidth={2.2} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <p className="text-sm text-black/50">
                {lang === "en" ? "Personal or business — we'll recommend Free, Plus, Premium or Pro." : "Bireysel veya işletme — Free, Plus, Premium ya da Pro önerelim."}
              </p>
            </div>
            <p className="mt-4 text-sm text-black/50">
              {lang === "en" ? "Curious about the product?" : "Ürünü yakından tanımak ister misiniz?"}{" "}
              <button
                onClick={() => goTo("/kitapcik", navigate)}
                data-testid="hero-kitapcik-btn"
                className="inline-flex items-center gap-1 font-bold text-operofy-blue transition-colors duration-200 hover:text-black"
              >
                {t.hero.ctaSecondary}
                <ArrowRight size={14} strokeWidth={2} />
              </button>
            </p>
          </FadeUp>
        </div>

        {/* Mobil/tablet ürün önizlemesi */}
        <FadeUp delay={0.2} className="mt-14 lg:hidden">
          <div data-testid="hero-screenshot">
            <BrowserFrame
              src={content.img}
              alt={lang === "en" ? "Parasyo web panel screen" : "Parasyo web panel ekranı"}
              loading="eager"
            />
          </div>
        </FadeUp>

        {/* Gri iş ortağı logoları */}
        <FadeUp delay={0.15}>
          <div className="mt-16 flex flex-wrap items-center justify-center gap-x-12 gap-y-5 border-t border-black/10 pt-10" data-testid="hero-partner-logos">
            <span className="font-display text-lg font-extrabold tracking-[0.18em] text-black/30" data-testid="partner-sovos">SOVOS</span>
            <span className="font-display text-lg font-extrabold tracking-[0.18em] text-black/30" data-testid="partner-cloudflare">Cloudflare</span>
            <img src="/assets/iyzico-ile-ode.png" alt="iyzico" width="1050" height="360" className="h-6 w-auto opacity-50" style={{ filter: "grayscale(1) brightness(0.35)" }} data-testid="partner-iyzico" loading="lazy" />
            <span className="font-display text-lg font-extrabold tracking-[0.18em] text-black/30" data-testid="partner-google">Google</span>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
